/**
 * 테마 토글 (라이트/다크) — localStorage persist + 시스템 prefers-color-scheme 첫 폴백.
 *
 * 초기 적용은 index.html head의 inline script가 처리한다(테마 변수가 CSS 로드 전에
 * documentElement에 박혀 있어야 light/dark 깜빡임 없음). 이 모듈은 토글 클릭과
 * 시스템 테마 변경(저장값이 없을 때)만 다룬다.
 */

const KEY = 'mdview:theme';

export type Theme = 'light' | 'dark';

function readSaved(): Theme | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function current(): Theme {
  return (document.documentElement.dataset.theme as Theme) ?? 'light';
}

function apply(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

export function setupTheme(
  button: HTMLElement,
  onChange?: (theme: Theme) => void,
): void {
  // 초기 상태는 inline script가 이미 적용했지만 안전망으로 한 번 더 동기화.
  if (!document.documentElement.dataset.theme) {
    apply(readSaved() ?? (systemPrefersDark() ? 'dark' : 'light'));
  }

  button.addEventListener('click', () => {
    const next: Theme = current() === 'dark' ? 'light' : 'dark';
    apply(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // 사파리 시크릿 모드 등 — 무시. 세션 내에서는 동작.
    }
    onChange?.(next);
  });

  // 사용자가 저장값을 두지 않은 동안에만 OS 테마 변경에 자동 반응.
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (readSaved()) return;
    const next: Theme = e.matches ? 'dark' : 'light';
    apply(next);
    onChange?.(next);
  });
}
